import { motion, AnimatePresence } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';

const getBotResponse = (message) => {
  const text = message.toLowerCase().trim();

  if (
    text.includes('precio') ||
    text.includes('precios') ||
    text.includes('cuánto cuesta') ||
    text.includes('cuanto cuesta') ||
    text.includes('coste') ||
    text.includes('cotización') ||
    text.includes('cotizacion')
  ) {
    return `Aquí tienes nuestros precios aproximados:

• Filmación: desde $60
• Edición de video: desde $50
• Paquete filmación + edición: desde $115
• Gestión de redes sociales: desde $140/mes
• Logo e identidad de marca: desde $189
• Desarrollo web: entre $750 y $2500

Si quieres, también puedes escribirnos a orianpartners07@gmail.com para una propuesta personalizada.`;
  }

  if (
    text.includes('servicio') ||
    text.includes('servicios') ||
    text.includes('qué hacen') ||
    text.includes('que hacen') ||
    text.includes('ofrecen')
  ) {
    return `Ofrecemos estos servicios:

• Video Production
• Social Media Management
• Brand Identity
• Website Development
• App Development
• AI Automation
• Full Package

Si quieres, cuéntame cuál te interesa y te orientaré mejor.`;
  }

  if (
    text.includes('fundador') ||
    text.includes('creador') ||
    text.includes('dueño') ||
    text.includes('dueno') ||
    text.includes('quién fundó') ||
    text.includes('quien fundo')
  ) {
    return 'Orian Partners fue fundado por Gerson Ortiz.';
  }

  if (
    text.includes('contacto') ||
    text.includes('correo') ||
    text.includes('email') ||
    text.includes('gmail') ||
    text.includes('escribir')
  ) {
    return 'Puedes escribirnos directamente a orianpartners07@gmail.com o usar el formulario de contacto en esta página.';
  }

  if (
    text.includes('ubicación') ||
    text.includes('ubicacion') ||
    text.includes('dónde están') ||
    text.includes('donde estan') ||
    text.includes('location')
  ) {
    return 'Trabajamos desde Santo Domingo, República Dominicana, y también de forma remota.';
  }

  if (
    text.includes('video gratis') ||
    text.includes('oferta') ||
    text.includes('promoción') ||
    text.includes('promocion') ||
    text.includes('gratis')
  ) {
    return 'Sí, en algunos casos ofrecemos propuestas promocionales o piezas iniciales según el proyecto. Escríbenos a orianpartners07@gmail.com y evaluamos tu caso.';
  }

  if (
    text.includes('automatización') ||
    text.includes('automatizacion') ||
    text.includes('ia') ||
    text.includes('inteligencia artificial') ||
    text.includes('chatbot')
  ) {
    return 'Podemos ayudarte con automatización, chatbots personalizados e integración de IA para negocios y marcas.';
  }

  if (
    text.includes('web') ||
    text.includes('página') ||
    text.includes('pagina') ||
    text.includes('sitio')
  ) {
    return 'Desarrollamos sitios web profesionales, páginas corporativas y soluciones digitales a medida, con precios desde $750 según alcance y complejidad.';
  }

  if (
    text.includes('redes') ||
    text.includes('social media') ||
    text.includes('instagram') ||
    text.includes('contenido')
  ) {
    return 'Gestionamos redes sociales, estrategia de contenido y posicionamiento digital para marcas que quieren crecer con intención.';
  }

  if (
    text.includes('hola') ||
    text.includes('buenas') ||
    text.includes('saludos') ||
    text === 'hi' ||
    text === 'hello'
  ) {
    return '¡Hola! Soy el asistente de Orian Partners. Puedo ayudarte con precios, servicios, desarrollo web, automatización con IA y contacto.';
  }

  return 'Puedo ayudarte con precios, servicios, automatización, desarrollo web y contacto. Si prefieres atención directa, escríbenos a orianpartners07@gmail.com.';
};

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      content:
        '¡Hola! 👋 Soy el asistente de Orian Partners. Puedo ayudarte con precios, servicios, automatización, desarrollo web y contacto.',
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const quickQuestions = [
    '¿Cuáles son los precios?',
    '¿Cuáles son los servicios?',
    '¿Quién es el fundador?',
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();

    setMessages((prev) => [
      ...prev,
      { role: 'user', content: userMessage },
    ]);

    setInput('');
    setIsLoading(true);

    setTimeout(() => {
      const botReply = getBotResponse(userMessage);

      setMessages((prev) => [
        ...prev,
        { role: 'bot', content: botReply },
      ]);

      setIsLoading(false);
    }, 700);
  };

  const handleQuickQuestion = (question) => {
    if (isLoading) return;

    setMessages((prev) => [
      ...prev,
      { role: 'user', content: question },
    ]);

    setIsLoading(true);

    setTimeout(() => {
      const botReply = getBotResponse(question);

      setMessages((prev) => [
        ...prev,
        { role: 'bot', content: botReply },
      ]);

      setIsLoading(false);
    }, 700);
  };

  return (
    <>
      <motion.button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 right-6 z-40 w-14 h-14 bg-[#D4AF37] hover:bg-white text-black rounded-full shadow-lg flex items-center justify-center transition-colors ${
          isOpen ? 'hidden' : ''
        }`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="chatbot-toggle"
      >
        <MessageCircle size={24} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[360px] h-[500px] bg-zinc-950 border border-white/10 shadow-2xl flex flex-col overflow-hidden max-w-[calc(100vw-24px)]"
            data-testid="chatbot-window"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-zinc-900">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                  <Bot size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-white">Asistente Orian</h4>
                  <span className="text-xs text-green-400 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    En línea
                  </span>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-zinc-500 hover:text-white transition-colors"
                data-testid="close-chat-btn"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'user' ? 'bg-white/10' : 'bg-[#D4AF37]/20'
                    }`}
                  >
                    {msg.role === 'user' ? (
                      <User size={14} className="text-white" />
                    ) : (
                      <Bot size={14} className="text-[#D4AF37]" />
                    )}
                  </div>

                  <div
                    className={`max-w-[80%] p-3 text-sm whitespace-pre-line ${
                      msg.role === 'user'
                        ? 'bg-[#D4AF37] text-black rounded-l-lg rounded-br-lg'
                        : 'bg-zinc-900 text-zinc-300 rounded-r-lg rounded-bl-lg'
                    }`}
                  >
                    {msg.content}
                  </div>
                </motion.div>
              ))}

              {isLoading && (
                <div className="flex gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <Bot size={14} className="text-[#D4AF37]" />
                  </div>
                  <div className="bg-zinc-900 p-3 rounded-r-lg rounded-bl-lg">
                    <div className="flex gap-1">
                      <span
                        className="w-2 h-2 bg-zinc-600 rounded-full animate-bounce"
                        style={{ animationDelay: '0ms' }}
                      />
                      <span
                        className="w-2 h-2 bg-zinc-600 rounded-full animate-bounce"
                        style={{ animationDelay: '150ms' }}
                      />
                      <span
                        className="w-2 h-2 bg-zinc-600 rounded-full animate-bounce"
                        style={{ animationDelay: '300ms' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {messages.length <= 2 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {quickQuestions.map((q) => (
                  <button
                    key={q}
                    onClick={() => handleQuickQuestion(q)}
                    className="text-xs px-3 py-1.5 bg-zinc-900 text-zinc-400 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 border border-white/10 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={sendMessage} className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Escribe tu mensaje..."
                  className="flex-1 bg-zinc-900 border border-white/10 focus:border-[#D4AF37] focus:outline-none px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 transition-colors"
                  data-testid="chat-input"
                />
                <motion.button
                  type="submit"
                  disabled={isLoading || !input.trim()}
                  className="w-10 h-10 bg-[#D4AF37] text-black flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="chat-send-btn"
                >
                  <Send size={16} />
                </motion.button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
